/**
 * Lab Player P/L (`PLStatementCtrl.viewTablePLDataProcess`) source routing
 * for Logs → CL Transfers.
 *
 * - same calendar day **and** toDate is today → Postgres (`searchCreditLimitLogs`)
 * - otherwise → BigQuery (`searchCreditLimitLogsBQ`)
 * - range span &gt; 93 days → reject (Lab toastr)
 */

import type { CreditLimitLogSource } from './clTransfersData'

export type ClTransfersClassicRoute = CreditLimitLogSource | 'reject_over_93'

const DAY_MS = 24 * 60 * 60 * 1000
export const LAB_CL_TRANSFERS_MAX_RANGE_DAYS = 93

/** YYYY-MM-DD in Lab operator TZ (IST). */
export function calendarDayKey(d: Date, timeZone = 'Asia/Kolkata'): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d)
}

export const LAB_CL_TRANSFERS_OVER_93_MESSAGE =
  'The date you have selected exceeds 93 days. Please try again.'

export const LAB_CL_TRANSFERS_DATES_REQUIRED_MESSAGE = 'Provide From and To Date'

export const LAB_CL_TRANSFERS_TO_BEFORE_FROM_MESSAGE =
  'To Date cannot be earlier than From Date'

export const LAB_CL_TRANSFERS_FUTURE_DATE_MESSAGE =
  'Dates cannot be in the future'

function dayUtcMs(day: string): number {
  return Date.parse(`${day.slice(0, 10)}T00:00:00Z`)
}

/**
 * Search guards (empty, order, future, 93-day span).
 * Returns toast copy, or null when the range is searchable.
 */
export function validateClTransfersClassicDates(
  fromDay: string,
  toDay: string,
  now = new Date(),
  timeZone = 'Asia/Kolkata',
): string | null {
  const fromKey = fromDay?.trim().slice(0, 10) ?? ''
  const toKey = toDay?.trim().slice(0, 10) ?? ''
  if (!fromKey || !toKey) return LAB_CL_TRANSFERS_DATES_REQUIRED_MESSAGE

  const fromUtc = dayUtcMs(fromKey)
  const toUtc = dayUtcMs(toKey)
  if (!Number.isFinite(fromUtc) || !Number.isFinite(toUtc)) {
    return LAB_CL_TRANSFERS_DATES_REQUIRED_MESSAGE
  }
  if (toUtc < fromUtc) return LAB_CL_TRANSFERS_TO_BEFORE_FROM_MESSAGE

  const todayKey = calendarDayKey(now, timeZone)
  const todayUtc = dayUtcMs(todayKey)
  if (fromUtc > todayUtc || toUtc > todayUtc) {
    return LAB_CL_TRANSFERS_FUTURE_DATE_MESSAGE
  }

  const diffDays = Math.ceil((toUtc - fromUtc) / DAY_MS)
  if (diffDays > LAB_CL_TRANSFERS_MAX_RANGE_DAYS) {
    return LAB_CL_TRANSFERS_OVER_93_MESSAGE
  }
  return null
}

/**
 * @param fromDay inclusive YYYY-MM-DD (date input)
 * @param toDay inclusive YYYY-MM-DD (date input)
 */
export function resolveClTransfersClassicSource(
  fromDay: string,
  toDay: string,
  now = new Date(),
  timeZone = 'Asia/Kolkata',
): ClTransfersClassicRoute {
  const fromKey = fromDay.slice(0, 10)
  const toKey = toDay.slice(0, 10)
  const todayKey = calendarDayKey(now, timeZone)
  const fromUtc = dayUtcMs(fromKey)
  const toUtc = dayUtcMs(toKey)
  if (!Number.isFinite(fromUtc) || !Number.isFinite(toUtc)) return 'postgres'
  if (toUtc < fromUtc) return 'reject_over_93'
  const diffDays = Math.ceil((toUtc - fromUtc) / DAY_MS)
  const isSameDayAsToday = todayKey === toKey && todayKey === fromKey
  if (diffDays <= 0 && isSameDayAsToday) return 'postgres'
  if (diffDays > LAB_CL_TRANSFERS_MAX_RANGE_DAYS) return 'reject_over_93'
  return 'bq'
}
